// Landing — editorial poster: kicker, big serif headline, single CTA.
// Deliberately spare; the seat map at /etkinlik is where the density lives.

import { SiteHeader } from '@/components/SiteHeader.tsx';
import { RESERVATION_OPENS_AT, reservationIsOpen } from '@okn/shared';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { formatOpensAt, useCountdownTo } from './ReservationOpening.tsx';

export function Home() {
  const { d, h, m, s, done } = useCountdownTo(RESERVATION_OPENS_AT);
  const open = done || reservationIsOpen();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    <div className="min-h-dvh">
      <SiteHeader />

      <main className="relative mx-auto flex max-w-6xl flex-col justify-center px-8 pt-20 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <div className="kicker mb-6">
            <span className="inline-block h-px w-10 align-middle bg-accent mr-3" />
            Acar Sanat Akademi
          </div>

          <h1 className="display text-[clamp(38px,6.4vw,90px)] leading-[0.98] font-light tracking-tight">
            Yıldızların Sanat Serüveni
            <br />
            <em className="font-normal italic text-accent">Bale ve Dans Gösterisi</em>
          </h1>

          <p className="mt-8 max-w-md text-[15px] leading-relaxed text-text-muted">
            Bu özel gösteri için koltuğunuzu seçin. Ön rezervasyonunuz oluşturulur;{' '}
            <span className="text-text">19 Haziran 19.30'a kadar</span> Acar Sanat Akademi'de onay
            görüşmesi ile rezervasyonunuz kesinleşir.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-5">
            <Link
              to="/etkinlik"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-md bg-accent px-7 py-3.5 text-[14px] font-medium tracking-wide text-white transition-all hover:bg-accent-hover hover:shadow-[0_8px_30px_-8px_var(--accent)]"
            >
              {open ? (
                <span>Yer Seç</span>
              ) : (
                <span className="tnum">
                  Açılışa {d > 0 ? `${d}g ` : ''}
                  {pad(h)}:{pad(m)}:{pad(s)}
                </span>
              )}
              <span aria-hidden className="text-base">
                →
              </span>
            </Link>

            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-text-faint">
              <span className="inline-block h-px w-8 bg-border-strong" />
              <span>
                {open ? '2 dakikada ön rezervasyon' : `Rezervasyon ${formatOpensAt()}'de açılıyor`}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="mt-24 grid max-w-2xl grid-cols-3 gap-px overflow-hidden rounded-md border border-border bg-border"
        >
          <Stat label="Koltuk" value="1.250" sub="aktif kapasite" />
          <Stat label="Onay" value="19 Haz" sub="son onay 19.30" />
          <Stat label="Hold" value="5dk" sub="seçim süresi" />
        </motion.div>

        <p className="mt-16 text-[11px] uppercase tracking-[0.22em] text-text-faint">
          Yönetim ·{' '}
          <a className="underline-offset-4 hover:text-text hover:underline" href="/admin">
            /admin
          </a>
        </p>
      </main>

      {/* Decorative serif backdrop watermark */}
      <div
        aria-hidden
        className="pointer-events-none fixed bottom-0 right-0 select-none overflow-hidden"
      >
        <div className="display translate-x-[12%] translate-y-[18%] text-[26vw] leading-none text-accent opacity-[0.025]">
          ACAR
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-bg-elevated px-5 py-5">
      <div className="kicker text-[10px]">{label}</div>
      <div className="mt-2 display text-2xl font-light tnum">{value}</div>
      <div className="mt-0.5 text-[11px] text-text-faint">{sub}</div>
    </div>
  );
}
